import { Divider, Flex, Group, IconFont, Text, Tooltip } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import {
    defaultFileMaxUploadSize,
    fileSizeFilter,
    getIsFindUseInVariable,
    InnerTypeMapByFieldType,
    ListItem4ByecodeUi,
    useAtomData,
    useFindUseObjectContext,
    useSpaceQuotaContext,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { lastPageOfStackAtom, pageAtomFamily, pageVariableSchemasFamily } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { uploadManagerInAppParams } from '@/utils/auth'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'

interface FileBlockSettingProps {
    blockId: string
}

const USED_FILE_TYPE = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf'])
const FIELD: Field = { type: 'file', id: '', dsId: '', name: '', innerType: InnerTypeMapByFieldType['file'] }

const FileBlockSetting: React.FunctionComponent<FileBlockSettingProps> = ({ blockId }) => {
    const styleProps = getBlockAndPageDesignLimit('file')
    const { control } = useFormContext()
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )
    const { quota } = useSpaceQuotaContext()
    const fileMaxUploadSize = quota?.uploadFileLimitSize ?? defaultFileMaxUploadSize
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))
    const findUseObject = useFindUseObjectContext()

    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name ?? '', [])
    )
    const uploadProps = useMemo(
        () => ({
            multiple: false,
            uploadOptions: {
                info: { id: '', label: pageName, groupId: pageId },
                options: {
                    ...uploadManagerInAppParams(),
                    fileFilter: fileSizeFilter(fileMaxUploadSize)
                }
            }
        }),
        [pageId, pageName, fileMaxUploadSize]
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const { customViewOption } = useVariableCustomViewOption()

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="设置">
                        <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                            <Flex gap={4}>
                                <Text>文件来源</Text>
                                <Tooltip title={`文件支持格式：${[...USED_FILE_TYPE].join('、')}`}>
                                    <IconFont type="Question" style={{ cursor: 'pointer' }} color="var(--color-gray-400)" />
                                </Tooltip>
                            </Flex>
                            <Controller
                                name="fileSource"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    const isHighLight = getIsFindUseInVariable({
                                        variable: value,
                                        findUseObject,
                                        pageVariableSchemas
                                    })
                                    return (
                                        <VariableSelect
                                            innerTypes={[FIELD.innerType]}
                                            field={FIELD}
                                            disablePexels
                                            highlighting={isHighLight}
                                            uploadProps={uploadProps}
                                            viewOptions={customViewOption ? [customViewOption] : undefined}
                                            pageOptions={pageOptions}
                                            userOption={userOption}
                                            value={value}
                                            onChange={onChange}
                                        />
                                    )
                                }}
                            />
                        </ListItem4ByecodeUi>
                    </Group>

                    <Divider />

                    <AnchorSetting />

                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}

export default FileBlockSetting
